import axios from "axios";
import { BiSolidCameraMovie } from "react-icons/bi";
import { GiArtificialIntelligence } from "react-icons/gi";
import { GoLightBulb } from "react-icons/go";
import { HiOutlineAcademicCap } from "react-icons/hi";
import { IoBusiness, IoNewspaper } from "react-icons/io5";
import { MdAutoStories, MdHealthAndSafety, MdOutlineSportsCricket } from "react-icons/md";

/**
 * ------------------------------------------------------------------
 * BASE URL / ENDPOINT CONFIG
 * ------------------------------------------------------------------
 * The backend now exposes ONE shared posts API (todattalkspost) and
 * ONE categories API (categories) for the Posts and Categories admin
 * pages. Categories on those pages are fetched dynamically.
 *
 * NOTE: the Overview page below is untouched and still runs on the
 * original CATEGORIES / OVERVIEW_URL setup — left exactly as-is,
 * not migrated to the new categories API.
 * ------------------------------------------------------------------
 */
export const API_BASE_URL = "https://todaytalks.mpdatahub.com/api";
export const ASSET_BASE_URL = "https://todaytalks.mpdatahub.com/";

// ---------------- Posts (Today Talks Post API) ----------------
export const POSTS_URL = `${API_BASE_URL}/todattalkspost`;
export const postUrl = (id) => `${POSTS_URL}/${id}`;
export const POSTS_STORE_URL = `${POSTS_URL}/store`;
export const postUpdateUrl = (id) => `${POSTS_URL}/update/${id}`;
export const postDeleteUrl = (id) => `${POSTS_URL}/delete/${id}`;
export const togglePostStatus = (id, status) => `${API_BASE_URL}/today-talks-posts/status?id=${id}&status=${status}`;
export const togglePostTrending = (id, trending) => `${API_BASE_URL}/today-talks-posts/trending?id=${id}&trending=${trending}`;

// ---------------- Categories API (Categories admin page) ----------------
export const CATEGORIES_URL = `${API_BASE_URL}/categories`;
export const categoryUrl = (id) => `${CATEGORIES_URL}/${id}`;
export const CATEGORIES_STORE_URL = `${API_BASE_URL}/categories-store`;
export const categoryUpdateUrl = (id) => `${API_BASE_URL}/categories/update/${id}`;
export const categoryDeleteUrl = (id) => `${API_BASE_URL}/categories/delete/${id}`;

export const AUTH_BASE_URL = API_BASE_URL;

/**
 * ------------------------------------------------------------------
 * ORIGINAL Overview-page config — untouched, exactly as it was.
 * ------------------------------------------------------------------
 */
export const CATEGORIES = [
  {
    key: "updates",
    label: "Updates",
    icon: <IoNewspaper />,
    endpoint: "https://todaytalks.mpdatahub.com/api/tech-updates",
  },
  {
    key: "business",
    label: "Business",
    icon: <IoBusiness />,
    endpoint: "https://todaytalks.mpdatahub.com/api/newsarticles",
  },
  {
    key: "education",
    label: "Education",
    icon: <HiOutlineAcademicCap />,
    endpoint: "https://todaytalks.mpdatahub.com/api/study-articles",
  },
  {
    key: "entertainment",
    label: "Entertainment",
    icon: <BiSolidCameraMovie />,
    endpoint: "https://todaytalks.mpdatahub.com/api/movienewsartcles",
  },
  {
    key: "health",
    label: "Health",
    icon: <MdHealthAndSafety />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getHealthArticles",
  },
  {
    key: "insights",
    label: "Insights",
    icon: <GoLightBulb />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getScientificFacts",
  },
  {
    key: "sports",
    label: "Sports",
    icon: <MdOutlineSportsCricket />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getSportsArticles",
  },
  {
    key: "stories",
    label: "Stories",
    icon: <MdAutoStories />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getShortStories",
  },
  {
    key: "technology",
    label: "Technology",
    icon: <GiArtificialIntelligence />,
    endpoint: "https://todaytalks.mpdatahub.com/api/technews",
  },
];

export const getCategoryByKey = (key) =>
  CATEGORIES.find((c) => c.key === key) || null;

// Returns { total_active, category_counts: { <CategoryLabel>: count, ... } }
export const OVERVIEW_URL = "https://todaytalks.mpdatahub.com/api/talks/overview";

/**
 * Turns a relative path returned by the API (e.g. "uploads/posts/x.jpg")
 * into a full, browser-loadable URL. Already-absolute URLs are left as-is.
 */
export function toAssetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${ASSET_BASE_URL}${String(path).replace(/^\/+/, "")}`;
}

// Only these emails are allowed to log in (checked client-side as an
// extra layer, on top of whatever the backend already enforces).
export const ALLOWED_EMAILS = [
  "vishnubharani153@gmail.com",
  "kavinperiyagounder@gmail.com",
  "kavin@gmail.com",
];

/**
 * The posts API requires a numeric `user_id`. The login response doesn't
 * reliably include one (see authApi.js), so we map known admin emails to
 * their backend user id here. ⚠️ Update these to match the real `users`
 * table — the fallback (DEFAULT_USER_ID) is just a placeholder.
 */
export const USER_IDS_BY_EMAIL = {
  "vishnubharani153@gmail.com": 20,
  "kavinperiyagounder@gmail.com": 20,
  "kavin@gmail.com": 20,
};
export const DEFAULT_USER_ID = 1;

export function getUserId(user) {
  if (!user) return DEFAULT_USER_ID;
  if (user.id) return user.id;
  if (user.user_id) return user.user_id;
  const email = (user.email || "").toLowerCase();
  return USER_IDS_BY_EMAIL[email] ?? DEFAULT_USER_ID;
}

export const TOKEN_KEY = "news_admin_token";
export const USER_KEY = "news_admin_user";

const api = axios.create({
  timeout: 20000,
});

// Attach token to every request if we have one.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 (expired/invalid token).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;
